import { keyBy } from 'es-toolkit';
import React, { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, IconButton, List, ListItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid';

import { ComponentPreview } from '@paragon/client-tools';
import { ComponentDesign } from '@paragon/contract-library';
import { NumericField } from '@paragon/material';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { calculateMemberGeometry } from '@paragon/relative-geometry';
import { naturalSort, notNullish } from '@paragon/utilities';

import { AssemblyGroupStarterData, createAssemblyGroups } from '../../../actions/assemblyGroupActions';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { CreateAssemblyGroupsDialogProps } from '../dialogs';
import { CreateAssemblyGroupsChooseSource } from './CreateAssemblyGroupsChooseSource';

export function CreateAssemblyGroupsDialog({ initialLookupCode }: CreateAssemblyGroupsDialogProps) {
  const dispatch = useAppDispatch();
  const assemblyGroups = useAppSelector((state) => state.assemblyGroups.data);

  const [stage, setStage] = useState<'choose-source' | 'build-groups'>('choose-source');
  const [componentDesigns, setComponentDesigns] = useState<readonly ComponentDesign[] | null>(null);
  const componentDesignsByGuid = keyBy(componentDesigns ?? [], (componentDesign) => componentDesign.guid);

  // This gets adjusted for queued Assembly Groups
  const [componentDesignGuidToQuantity, setComponentDesignGuidToQuantity] = useState<Record<string, number>>({});

  const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>([]),
  });
  function setComponentDesignSelection(componentDesignGuids: readonly string[]) {
    setRowSelectionModel({ type: 'include', ids: new Set(componentDesignGuids) });
  }
  const selectedComponentDesignGuids =
    rowSelectionModel.type === 'include'
      ? Array.from(rowSelectionModel.ids)
      : Object.keys(componentDesignGuidToQuantity).filter((guid) => !rowSelectionModel.ids.has(guid));
  const [componentDesignGuidToQuantityToInclude, setComponentDesignGuidToQuantityToInclude] = useState<
    Record<string, number>
  >({});
  function setComponentDesignsAndInfo(
    componentDesigns: readonly ComponentDesign[] | null,
    componentDesignGuidToQuantity: Record<string, number>,
    inferredGroupName: string | null,
  ) {
    setComponentDesigns(componentDesigns);
    setComponentDesignGuidToQuantity(componentDesignGuidToQuantity);
    setComponentDesignSelection(componentDesigns?.map((componentDesign) => componentDesign.guid) ?? []);
    setComponentDesignGuidToQuantityToInclude(componentDesignGuidToQuantity);
    if (name === '' && inferredGroupName) setName(inferredGroupName);
  }
  const [name, setName] = useState<string>('');
  const [notes, setNotes] = useState<string | null>(null);
  const [queuedAssemblyGroups, setQueuedAssemblyGroups] = useState<readonly AssemblyGroupStarterData[]>([]);

  const [didFail, setDidFail] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const nameProblem = queuedAssemblyGroups
    .concat(Object.values(assemblyGroups))
    .some((existingGroup) => existingGroup.name === name.trim())
    ? 'Assembly Group with same name already exists'
    : null;

  const columns: GridColDef<ComponentDesign>[] = [
    {
      field: 'name',
      headerName: 'Name',
      sortComparator: naturalSort,
      width: 200,
      valueGetter: (value, componentDesign) => componentDesign.component.name,
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      width: 150,
      valueGetter: (value, componentDesign) => componentDesignGuidToQuantity[componentDesign.guid] ?? 'unknown',
    },
    {
      field: 'quantity-to-include',
      headerName: 'Quantity to Include',
      width: 150,
      renderCell: (params) => (
        <NumericField
          value={componentDesignGuidToQuantityToInclude[params.row.guid] ?? 0}
          onSubmit={(value) =>
            setComponentDesignGuidToQuantityToInclude((oldState) => ({ ...oldState, [params.row.guid]: value }))
          }
          onClick={(event) => void event.stopPropagation()}
          integers
          min={1}
          disabled={!selectedComponentDesignGuids.includes(params.row.guid)}
          margin="none"
          sx={{ verticalAlign: 'middle' }}
        />
      ),
    },
    {
      field: 'preview',
      headerName: 'Preview',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <ComponentPreview
          memberVertices={params.row.component.members
            .map((member) => calculateMemberGeometry(member, params.row.component))
            .filter(notNullish)}
          maxWidth={100}
          maxHeight={50}
          sx={{ verticalAlign: 'middle', textAlign: 'center' }}
        />
      ),
    },
  ];

  const componentDesignGuidsWhereQuantityToIncludeExceedsQuantity = selectedComponentDesignGuids.filter(
    (componentDesignGuid) =>
      componentDesignGuidToQuantityToInclude[componentDesignGuid] > componentDesignGuidToQuantity[componentDesignGuid],
  );
  const includedComponentDesignGuids = selectedComponentDesignGuids.filter(
    (componentDesignGuid) => componentDesignGuidToQuantityToInclude[componentDesignGuid] > 0,
  );

  function convertInputsToAssemblyGroupProperties(): AssemblyGroupStarterData | null {
    if (name.trim() === '' || includedComponentDesignGuids.length === 0) {
      return null;
    }

    return {
      name: name.trim(),
      notes: notes === '' ? null : notes,
      componentDesigns: Object.fromEntries(
        includedComponentDesignGuids.map((componentDesignGuid) => [
          componentDesignGuid,
          {
            index: null,
            quantity: componentDesignGuidToQuantityToInclude[componentDesignGuid],
          },
        ]),
      ),
    };
  }

  async function onSubmit() {
    setDidFail(false);

    const assemblyGroupFromCurrentSelection = convertInputsToAssemblyGroupProperties();
    if (queuedAssemblyGroups.length === 0 && assemblyGroupFromCurrentSelection === null) {
      throw new Error('Submit attempted with no assembly groups defined; the UI should have prevented this');
    }

    const assemblyGroupStarterDataList =
      queuedAssemblyGroups.length > 0 ? queuedAssemblyGroups : [assemblyGroupFromCurrentSelection!];

    try {
      const success = await dispatch(createAssemblyGroups(assemblyGroupStarterDataList));
      if (!success) {
        setDidFail(true);
        return;
      }
    } catch (error) {
      setDidFail(true);

      console.error(error);

      return;
    }

    dispatch(closeCurrentPopup());
  }

  const currentInputConstitutesAssemblyGroupStarterData =
    name !== '' && !nameProblem && includedComponentDesignGuids.length > 0;
  const areAssemblyGroupsDefined = queuedAssemblyGroups.length > 0 || currentInputConstitutesAssemblyGroupStarterData;
  const submitDisabled = isWorking || !areAssemblyGroupsDefined;

  return (
    <Dialog
      title="Create Assembly Group(s)"
      submitText={queuedAssemblyGroups.length > 0 ? 'Create All Queued' : 'Create'}
      onSubmit={onSubmit}
      onCancel="close"
      enterKeySubmits
      disabled={isWorking}
      submitDisabled={submitDisabled}
      hasSpinner={isWorking}
    >
      <Stack spacing={2} sx={{ marginTop: 1 }}>
        {didFail && (
          <Alert variant="outlined" severity="error">
            Unable to create assembly group.
          </Alert>
        )}

        {stage === 'choose-source' && (
          <CreateAssemblyGroupsChooseSource
            initialLookupCode={initialLookupCode}
            componentDesigns={componentDesigns}
            setComponentDesignsAndInfo={setComponentDesignsAndInfo}
            setIsWorking={setIsWorking}
            setFinishedChoosingSource={() => void setStage('build-groups')}
          />
        )}

        {stage === 'build-groups' && (
          <>
            {componentDesigns && (
              <>
                <Box sx={{ height: 300, width: '100%' }}>
                  <DataGrid
                    rows={componentDesigns.filter(({ guid }) => guid in componentDesignGuidToQuantity)}
                    getRowId={(row) => row.guid}
                    columns={columns}
                    checkboxSelection
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={setRowSelectionModel}
                    initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
                  />
                </Box>
                {componentDesignGuidsWhereQuantityToIncludeExceedsQuantity.length > 0 && (
                  <Alert variant="outlined" severity="warning">
                    Quantity to Include exceeds Quantity for:{' '}
                    {componentDesignGuidsWhereQuantityToIncludeExceedsQuantity
                      .map((componentDesignGuid) => componentDesignsByGuid[componentDesignGuid].component.name)
                      .join(', ')}
                  </Alert>
                )}
              </>
            )}
            <TextField
              autoFocus
              autoComplete="off"
              size="small"
              label="Name"
              sx={{ marginTop: 1, width: '400px' }}
              value={name}
              error={!!nameProblem}
              onFocus={(event) => event.target.select()}
              onChange={(event) => void setName(event.target.value)}
            />
            {nameProblem && (
              <Alert variant="outlined" severity="error">
                {nameProblem}
              </Alert>
            )}
            <TextField
              autoFocus
              autoComplete="off"
              size="small"
              multiline
              label="Notes"
              sx={{ marginTop: 1, width: '400px' }}
              value={notes ?? ''}
              onFocus={(event) => event.target.select()}
              onChange={(event) => void setNotes(event.target.value)}
            />

            <Button
              variant="outlined"
              onClick={() => {
                const assemblyGroupFromCurrentSelection = convertInputsToAssemblyGroupProperties();
                if (assemblyGroupFromCurrentSelection === null) {
                  throw new Error(
                    'Attempted to queue Assembly Group with incomplete definition; the UI should have prevented this',
                  );
                }
                setQueuedAssemblyGroups((oldState) =>
                  assemblyGroupFromCurrentSelection ? oldState.concat([assemblyGroupFromCurrentSelection]) : oldState,
                );
                setName('');
                setNotes(null);
                setComponentDesignSelection([]);
                const updatedComponentDesignQuantities = { ...componentDesignGuidToQuantity };
                for (const [componentDesignGuid, { quantity }] of Object.entries(
                  assemblyGroupFromCurrentSelection.componentDesigns,
                )) {
                  const quantityRemaining = componentDesignGuidToQuantity[componentDesignGuid] - (quantity ?? 0);
                  if (quantityRemaining > 0) {
                    updatedComponentDesignQuantities[componentDesignGuid] = quantityRemaining;
                  } else {
                    delete updatedComponentDesignQuantities[componentDesignGuid];
                  }
                }
                setComponentDesignGuidToQuantity(updatedComponentDesignQuantities);
                setComponentDesignGuidToQuantityToInclude(updatedComponentDesignQuantities);
              }}
              disabled={!currentInputConstitutesAssemblyGroupStarterData}
            >
              Queue Assembly Group
            </Button>

            {queuedAssemblyGroups.length > 0 && (
              <Paper>
                <Typography variant="h5" sx={{ p: 2 }}>
                  Queued Assembly Groups:
                </Typography>
                <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {queuedAssemblyGroups.map((queuedGroup) => (
                    <ListItem
                      key={queuedGroup.name}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => {
                            const updatedComponentDesignQuantities = { ...componentDesignGuidToQuantity };
                            for (const [componentDesignGuid, { quantity }] of Object.entries(
                              queuedGroup.componentDesigns,
                            )) {
                              updatedComponentDesignQuantities[componentDesignGuid] =
                                (updatedComponentDesignQuantities[componentDesignGuid] ?? 0) + (quantity ?? 0);
                            }
                            setComponentDesignGuidToQuantity(updatedComponentDesignQuantities);
                            setComponentDesignGuidToQuantityToInclude(updatedComponentDesignQuantities);
                            setQueuedAssemblyGroups((oldState) =>
                              oldState.filter((oldGroup) => oldGroup.name !== queuedGroup.name),
                            );
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <Typography>{queuedGroup.name}</Typography>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </>
        )}
      </Stack>
    </Dialog>
  );
}
